<template>
  <div
    class="wrap"
    :class="[
      { 'loading-cursor': isLoadingData },
      uiTheme === 'executive' && 'theme-executive',
      isCommandTheme && 'theme-command',
      uiTheme === 'command11' && 'theme-command-palette-11',
      uiTheme === 'command39' && 'theme-command-palette-39',
    ]"
  >
    <div class="header">
      <div class="title" :title="settings.subdomain">
        <h1>Панель активности</h1>
        <div v-if="viewMode === 'period'" class="title-subtitle">
          Управленческий режим за {{ selectedPeriodDays.length }}
          {{ getDayWord(selectedPeriodDays.length) }}
        </div>
      </div>

      <div class="tabs">
        <div
          v-if="viewMode === 'day' && dayDrilldownContext"
          class="tabs-item tabs-drilldown-back"
        >
          <button
            type="button"
            class="drilldown-back"
            aria-label="Вернуться к выбранному периоду и восстановить фильтры"
            @click="returnToPeriod"
          >
            <span aria-hidden="true">&larr;</span>
            Назад к периоду
          </button>
        </div>
        <div class="tabs-item tabs-mode">
          <button
            type="button"
            class="mode-toggle"
            :class="{ active: viewMode === 'day' }"
            @click="setViewMode('day')"
          >
            День
          </button>
          <button
            type="button"
            class="mode-toggle"
            :class="{ active: viewMode === 'period' }"
            @click="setViewMode('period')"
          >
            Период
          </button>
        </div>
        <div class="tabs-item tabs-period">
          <DatePicker
            @input="onDateSelection"
            :value-from="select__dayFrom"
            :value-to="select__dayTo"
            :selection-mode="viewMode === 'period' ? 'range' : 'single'"
            :show-mode-toggle="false"
            class="tabs-day"
            :class="{ 'loading-cursor': isLoadingData }"
          ></DatePicker>
        </div>
        <div v-if="viewMode === 'period'" class="tabs-item tabs-presets">
          <button
            type="button"
            class="period-preset"
            :class="{ active: periodPreset === 'week' }"
            @click="applyPeriodPreset('week')"
          >
            Неделя
          </button>
          <button
            type="button"
            class="period-preset"
            :class="{ active: periodPreset === 'month' }"
            @click="applyPeriodPreset('month')"
          >
            Месяц
          </button>
        </div>
        <div class="tabs-item">
          <select class="tabs-users" v-model="select__users" multiple>
            <optgroup
              v-for="deps in usersByDeps"
              :label="deps.name"
              :key="deps.id"
            >
              <option
                v-for="user in deps.users"
                :value="user.id"
                :key="user.id"
                selected="selected"
              >
                {{ user.name }}
              </option>
            </optgroup>
          </select>
        </div>
        <div class="tabs-item select-event-types">
          <select
            class="tabs-event_types"
            v-model="select__eventTypes"
            multiple
          >
            <option
              v-for="eventType in eventTypes"
              :value="eventType.id"
              :key="eventType.id"
              selected="selected"
            >
              {{ eventType.title }}
            </option>
          </select>
        </div>
        <div v-if="viewMode === 'period'" class="tabs-item tabs-weekends">
          <label class="toggle-checkbox">
            <input type="checkbox" v-model="includeWeekends" />
            <span>Учитывать выходные</span>
          </label>
        </div>
        <div v-if="viewMode === 'period'" class="tabs-item tabs-calls-filter">
          <label class="inline-input">
            <span>Звонок от</span>
            <input
              class="native-number native-select-small"
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              placeholder="0"
              title="Минимальная длительность звонка в секундах"
              v-model.number="minCallDurationSec"
            />
            <span>сек</span>
          </label>
        </div>
        <div
          v-if="viewMode === 'day' || viewMode === 'period'"
          class="tabs-item tabs-local-settings"
        >
          <button
            type="button"
            class="settings-toggle"
            :class="{ active: isSettingsPanelOpen }"
            :title="settingsSummaryTitle"
            @click.stop="toggleSettingsPanel"
          >
            Настройки
          </button>
          <div v-if="isSettingsPanelOpen" class="settings-popover" @click.stop>
            <div class="settings-popover-head">
              <strong>{{ viewMode === 'period' ? 'Настройки периода' : 'Настройки панели' }}</strong>
            </div>

            <div v-if="viewMode === 'day'" class="settings-grid">
              <label>
                <span>Начало дня</span>
                <input type="time" v-model="localPanelSettings.workStart" />
              </label>
              <label>
                <span>Конец дня</span>
                <input type="time" v-model="localPanelSettings.workEnd" />
              </label>
              <label>
                <span>Часовой пояс</span>
                <select v-model="localPanelSettings.timezone">
                  <option
                    v-for="timezone in availableTimezoneOptions"
                    :key="timezone"
                    :value="timezone"
                  >
                    {{ timezone }}
                  </option>
                </select>
              </label>
              <label>
                <span
                  class="settings-label-with-info"
                >
                  CRM +/- мин
                  <i
                    class="info-dot"
                    title="CRM-событие рисуется интервалом: указанное число минут до факта события и столько же после."
                    aria-label="Подсказка по CRM"
                  >i</i>
                </span>
                <input
                  type="number"
                  min="0"
                  max="60"
                  step="1"
                  v-model.number="localPanelSettings.simpleActivePaddingMin"
                />
              </label>
              <label>
                <span
                  class="settings-label-with-info"
                >
                  Сообщения +/- мин
                  <i
                    class="info-dot"
                    title="Сообщение не имеет длительности, поэтому рисуется интервалом: указанное число минут до времени сообщения и столько же после."
                    aria-label="Подсказка по сообщениям"
                  >i</i>
                </span>
                <input
                  type="number"
                  min="0"
                  max="30"
                  step="1"
                  v-model.number="localPanelSettings.messagePaddingMin"
                />
              </label>
              <label>
                <span>Тема</span>
                <select v-model="localPanelSettings.themeMode" @change="applyPanelTheme()">
                  <option value="dark">Темная</option>
                  <option value="light">Светлая</option>
                </select>
              </label>
            </div>

            <div v-else class="settings-grid settings-grid-period">
              <label>
                <span>В норме от, %</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  v-model.number="localPanelSettings.periodGoodPercent"
                />
              </label>
              <label>
                <span>Проседают от, %</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  v-model.number="localPanelSettings.periodWarningPercent"
                />
              </label>
              <label>
                <span>Критично ниже</span>
                <input
                  type="text"
                  :value="periodCriticalLabel"
                  readonly
                />
              </label>
              <label>
                <span>Тема</span>
                <select v-model="localPanelSettings.themeMode" @change="applyPanelTheme()">
                  <option value="dark">Темная</option>
                  <option value="light">Светлая</option>
                </select>
              </label>
            </div>

            <div class="settings-actions">
              <button
                v-if="wazzupAvailable"
                type="button"
                class="settings-secondary settings-wazzup"
                @click="openWazzupSettings"
              >
                Подключить Wazzup
              </button>
              <button type="button" class="settings-primary" @click="applyLocalPanelSettings">
                Применить
              </button>
            </div>
          </div>
        </div>
        <div class="tabs-item tabs-help">
          <button
            type="button"
            class="help-toggle"
            :class="{ active: isHelpPanelOpen }"
            title="Как читать панель"
            aria-label="Как читать панель"
            @click.stop="toggleHelpPanel"
          >
            ?
          </button>

          <div v-if="isHelpPanelOpen" class="help-popover" @click.stop>
            <div class="help-popover-head">
              <div>
                <strong>{{ isPeriodMode ? 'Режим «Период»' : 'Режим «День»' }}</strong>
              </div>
              <button
                type="button"
                class="help-close"
                aria-label="Закрыть инструкцию"
                @click="isHelpPanelOpen = false"
              >
                ×
              </button>
            </div>

            <template v-if="!isPeriodMode">
              <div class="help-section">
                <p>
                  Этот режим показывает, как прошел рабочий день каждого
                  сотрудника.
                </p>
                <p>
                  Выберите дату вверху панели. При необходимости оставьте
                  только нужных сотрудников или конкретные типы событий:
                  звонки, задачи, CRM, почту, переписку.
                </p>
                <p>
                  Напротив каждого сотрудника отображается временная шкала дня.
                  По ней видно, когда человек был активен, когда звонил,
                  переписывался, а когда в системе не было зафиксированных
                  действий.
                </p>
              </div>

              <div class="help-section">
                <h2>Цвета</h2>
                <ul v-if="isCommandTheme" class="help-color-list">
                  <li>
                    <span class="help-swatch help-swatch-active"></span>
                    <strong>Зеленый</strong>
                    <span>работа в CRM, задачах, почте и других рабочих событиях.</span>
                  </li>
                  <li>
                    <span class="help-swatch help-swatch-calls"></span>
                    <strong>Голубой</strong>
                    <span>звонки.</span>
                  </li>
                  <li>
                    <span class="help-swatch help-swatch-messages"></span>
                    <strong>Желтый</strong>
                    <span>переписка.</span>
                  </li>
                  <li>
                    <span class="help-swatch help-swatch-inactive"></span>
                    <strong>Розовый</strong>
                    <span>нет зафиксированной активности.</span>
                  </li>
                  <li>
                    <span class="help-swatch help-swatch-offhours"></span>
                    <strong>Серый</strong>
                    <span>время вне рабочего графика.</span>
                  </li>
                </ul>
                <ul v-else class="help-color-list">
                  <li>
                    <span class="help-swatch help-swatch-active"></span>
                    <strong>Зеленый</strong>
                    <span>работа в CRM, задачах, почте и других рабочих событиях.</span>
                  </li>
                  <li>
                    <span class="help-swatch help-swatch-calls"></span>
                    <strong>Голубой</strong>
                    <span>звонки.</span>
                  </li>
                  <li>
                    <span class="help-swatch help-swatch-messages"></span>
                    <strong>Фиолетовый</strong>
                    <span>переписка.</span>
                  </li>
                  <li>
                    <span class="help-swatch help-swatch-inactive"></span>
                    <strong>Красный</strong>
                    <span>нет зафиксированной активности.</span>
                  </li>
                  <li>
                    <span class="help-swatch help-swatch-offhours"></span>
                    <strong>Серый</strong>
                    <span>время вне рабочего графика.</span>
                  </li>
                </ul>
              </div>

              <div class="help-section help-section-note">
                <p>
                  В настройках можно выбрать светлую или темную тему, задать
                  рабочее время, часовой пояс и настроить, как долго событие
                  считается активностью на шкале.
                </p>
                <p>
                  <strong>Совет руководителю:</strong> смотрите не только на
                  красные промежутки, а на контекст дня. Например, длинная
                  пауза может быть встречей, обучением или работой вне CRM.
                </p>
              </div>
            </template>

            <template v-else>
              <div class="help-section">
                <p>
                  Этот режим помогает оценить активность сотрудников за
                  несколько дней: неделю, месяц или выбранный вручную период.
                </p>
                <p>
                  Выберите диапазон дат и нужных сотрудников. Панель покажет
                  сводную картину по дням: где активность была нормальной, где
                  снижалась, а где почти отсутствовала.
                </p>
              </div>

              <div class="help-section">
                <h2>Heatmap</h2>
                <p>В блоке Heatmap каждый день окрашен по уровню активности:</p>
                <ul class="help-color-list">
                  <li>
                    <span class="help-swatch help-swatch-normal"></span>
                    <strong>В норме</strong>
                    <span>сотрудник был достаточно активен.</span>
                  </li>
                  <li>
                    <span class="help-swatch help-swatch-warning"></span>
                    <strong>Проседают</strong>
                    <span>активности было меньше ожидаемого уровня.</span>
                  </li>
                  <li>
                    <span class="help-swatch help-swatch-critical"></span>
                    <strong>Критично</strong>
                    <span>активности почти не было.</span>
                  </li>
                </ul>
                <p>
                  Можно нажать на любой день в Heatmap, чтобы открыть подробную
                  дневную шкалу по выбранному сотруднику.
                </p>
              </div>

              <div class="help-section help-section-note">
                <p>
                  В настройках периода можно изменить пороги оценки: какой
                  процент активности считать нормой, просадкой или критичным
                  уровнем. Также можно включить или исключить выходные из
                  анализа и задать минимальную длительность звонка, который
                  нужно учитывать.
                </p>
                <p>
                  <strong>Совет руководителю:</strong> используйте период не
                  для разовых выводов, а для поиска закономерностей. Если
                  сотрудник часто попадает в «Критично», стоит обсудить
                  причины: нет задач, работа ведется вне CRM, не фиксируются
                  действия или действительно есть просадка в активности.
                </p>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <div class="content">
      <div v-if="loadError" class="load-error">
        {{ loadError }}
      </div>

      <PeriodDashboard
        v-if="isPeriodMode"
        :team-summary="periodDashboard.teamSummary"
        :ranking="filteredRanking"
        :rows="filteredHeatmapRows"
        :days="selectedPeriodDays"
        :team-trend="periodDashboard.teamTrend"
        :insights="periodDashboard.insights"
        :selected-employee-id="focusedEmployeeId"
        :employee-card="selectedEmployeeCard"
        :focused-day="focusedHeatmapDay"
        :status-filter="periodStatusFilter"
        :call-threshold-sec="minCallDurationSec"
        @summary-filter="periodStatusFilter = $event"
        @focus-employee="focusEmployee"
        @focus-day="focusHeatmapDay"
        @open-day="openDayDrilldown"
        @apply-insight="applyInsight"
        @clear-focus="clearPeriodFocus"
      />

      <div class="groups" v-else>
        <div class="group-wrapper" v-for="deps in visibleUsersByDeps" :key="deps.id">
          <div class="group">
            <div class="group-title">{{ deps.name }}</div>
            <div class="users">
              <div v-for="user in deps.users" :key="user.id">
                <div class="user" v-if="select__users.includes(user.id)">
                  <!-- {{ user }} -->
                  <div class="wrapper-user-title">
                    <div class="user-title">{{ user.name }}</div>

                    <div
                      :class="[
                        'downtime',
                        this.settings.customEventsEnabled &&
                          'enabled-custom-event',
                      ]"
                    >
                      <span
                        class="statText"
                        :title="
                          'Активен: ' +
                            getFormattedTimeDuration(
                              segmentsSumDuration[user.id],
                              optionsTimeHumanize.forStatTitle
                            ) +
                            `. В процент входят CRM (+/- ${localPanelSettings.simpleActivePaddingMin} мин), звонки по факту и сообщения +/- ${localPanelSettings.messagePaddingMin} мин.`
                        "
                        ><span
                          class="simpleActiveSymbol"
                          :title="`CRM: +/- ${localPanelSettings.simpleActivePaddingMin} мин вокруг события`"
                          >◼</span
                        >{{
                          !settings.showTotalInTime
                            ? utils.getPercent(
                                segmentsSumDuration[user.id],
                                getNowWidthScale(user.id)
                              ) + "%"
                            : "Активен: " +
                              getFormattedTimeDuration(
                                segmentsSumDuration[user.id],
                                optionsTimeHumanize.forStatTitle
                              )
                        }}</span
                      >
                      <span
                        class="statText stat-calls"
                        :title="
                          'Созвоны: ' +
                            getFormattedTimeDuration(
                              segmentsSumCallDuration[user.id],
                              optionsTimeHumanize.forStatTitle
                            ) +
                            '. Берется реальная длительность звонка.'
                        "
                        ><span
                          :class="'symbol-' + 'calls'"
                          title="Звонки: реальная длительность, без добавления минут"
                          >◼</span
                        >{{
                          formatMinutesDuration(segmentsSumCallDuration[user.id])
                        }}</span
                      >
                      <span
                        class="statText stat-messages"
                        :title="
                          'Сообщения: ' + (segmentsMessageCount[user.id] || 0) + ` шт. Каждое сообщение дает +/- ${localPanelSettings.messagePaddingMin} мин.`
                        "
                        ><span
                          class="symbol-messages"
                          :title="`Сообщения: +/- ${localPanelSettings.messagePaddingMin} мин вокруг события`"
                          >◼</span
                        >{{ segmentsMessageCount[user.id] || 0 }} шт.</span
                      >

                      <span
                        v-if="this.settings.customEventsEnabled"
                        class="statText stat-meeting"
                        :title="
                          getTypeTitleById(-1) +
                            ': ' +
                            getFormattedTimeDuration(
                              segmentsSumMeetingDuration[user.id],
                              optionsTimeHumanize.forStatTitle
                            )
                        "
                        ><span :class="'symbol-' + 'meeting'">◼</span
                        >{{
                          getFormattedTimeDuration(
                            segmentsSumMeetingDuration[user.id],
                            optionsTimeHumanize.forStat
                          )
                        }}</span
                      >

                      <span
                        v-if="this.settings.customEventsEnabled"
                        class="statText stat-lunch_time"
                        :title="
                          getTypeTitleById(-2) +
                            ': ' +
                            getFormattedTimeDuration(
                              segmentsSumLunchTimeDuration[user.id],
                              optionsTimeHumanize.forStatTitle
                            )
                        "
                        ><span :class="'symbol-lunch_time'">◼</span
                        >{{
                          getFormattedTimeDuration(
                            segmentsSumLunchTimeDuration[user.id],
                            optionsTimeHumanize.forStat
                          )
                        }}</span
                      >

                      <span
                        class="statText"
                        :title="
                          'Неактивен: ' +
                            getFormattedTimeDuration(
                              getNowWidthScale(user.id) -
                                segmentsSumDuration[user.id],
                              optionsTimeHumanize.forStatTitle
                            )
                        "
                        ><span class="inactiveSymbol">◼</span
                        >{{
                          !settings.showTotalInTime
                            ? utils.getPercent(
                                getNowWidthScale(user.id) -
                                  segmentsSumDuration[user.id],
                                getNowWidthScale(user.id)
                              ) + "%"
                            : "Неактивен: " +
                              getFormattedTimeDuration(
                                getNowWidthScale(user.id) -
                                  segmentsSumDuration[user.id],
                                optionsTimeHumanize.forStatTitle
                              )
                        }}</span
                      >
                    </div>
                  </div>

                  <div class="tooltip-wrap">
                    <div class="tooltip"></div>

                    <div class="segments">
                      <div
                        v-for="(segment, index) in segments[user.id]"
                        :key="index"
                        :class="['segment-item', 'segment-' + segment.group]"
                        :style="{
                          left: segment.left + '%',
                          width: segment.width + '%',
                        }"
                        :data-time="
                          getFormattedTimeDuration(
                            segment.duration,
                            optionsTimeHumanize.forSegmentTooltip
                          )
                        "
                        :data-group="segment.group"
                        :data-type="getTypeTitleById(segment.typeId)"
                      ></div>
                      <div
                        v-for="(segment, index) in messageSegments[user.id]"
                        :key="'message-' + index"
                        class="segment-item segment-messages"
                        :style="{
                          left: segment.left + '%',
                          width: segment.width + '%',
                        }"
                        :data-time="
                          getFormattedTimeDuration(
                            segment.duration,
                            optionsTimeHumanize.forSegmentTooltip
                          )
                        "
                        data-group="messages"
                        :data-type="getMessageSegmentType(segment)"
                      ></div>
                      <div
                        v-for="(segmentRed, index) in segmentsInactive[user.id]"
                        :key="index"
                        class="segment-item segment-inactive"
                        :style="{
                          left: segmentRed.left + '%',
                          width: segmentRed.width + '%',
                        }"
                        :data-time="
                          getFormattedTimeDuration(
                            segmentRed.duration,
                            optionsTimeHumanize.forSegmentTooltip
                          )
                        "
                        data-group="inactive"
                      ></div>
                    </div>
                  </div>

                  <div class="scale">
                    <div class="scale-line">
                      <div
                        class="scale-line-hour"
                        v-for="(scaleMark, index) in scaleMarks[user.id]"
                        :key="index"
                        :style="{ left: scaleMark.style.left }"
                        :class="{
                          firstScale: scaleMark.class.isFirstScale,
                          lastScale: scaleMark.class.isLastScale,
                        }"
                      >
                        <span class="scale-line-hour-time">
                          <span>{{ scaleMark.title }}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="footer">
        <a class="footer-link" target="_blank" href="http://simsales.ru/">
          <img
            class="footer-image footer-image-dark"
            src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAZ0AAABRCAMAAAA+XIvrAAAAY1BMVEUAAAD///////////////////////////////////////////////////8Sw/8Sw/////////8Sw/8Sw/8Sw/8Sw/8Sw/8Sw/8Sw/8Sw/8Sw/8Sw/8Sw/8Sw/8Sw/////8Sw/8oKlPUAAAAH3RSTlMAMEAgv4AQcK9gn1CP34Bg78+fUM8Qv3AgQDDvr4/fxJntywAACcVJREFUeNrtnGuboyoMgOsFL6itttPd6bRb/f+/8hC0CiEgnen2uM9DPnWUa14IIeDsdkGCBAkSJEiQIEGCBAkS5F0SxdG7q0zi+P/u9b8iaZ++u0rW9/93r/8VCXS2LIHOliXQ2bIEOluWQGfLEuhsWQKdLUug87xkjOXvqSnQeV6Kvu/Lt9QU6DwvqaDD3lRToPOscEGnektNgc7zkpXsTXHcQGfLEuhsWQKdLUugs2UJdLYsgc6WJdBxSlynTZPWyXrKiKVCav2WRiUfFrkrf8wKSMQpL91OJyqZNVfFodF5Zsuac8iasopKYaeTlLKlBZUv8tbTmuwPvikziAqAtHpcTQkVxH0P+imbKWWfznwy1vbz04QuIOFLmr41emehk7BGycX1XMncaCralKlZ+yLWeuGgk+Udke/xsni8+HEI5TgMJ084HWiszEGDtYuOVEjKGOPN0sIK9N7VjNWyXyVRQAJRh77jIicrJCamD0uSTsak7qG+eiTBlVyJKKepS0YHNHJZC2Sd8o6DaZWOzNem0Ju01QehQ0/fgvNx9kvKH/0WY0PTEqITtYtaY9FSDj9K0No0qpMCDavxr1Lka9k88iNQaKOZRopOBCOAz8kqKLutlCxTo8WQwSNZ2oJ0TpuVj8G0QieB8VXM+WRD+8Wc1KLQUU/8Z+vk+Zc/nEQM6xmUg44YV6pOuWx4palMsipRAdDHWpsrMAfVXBQdKKlIcK5ZV5F4O7cE0YFR1Og2CQaI0KybDuTrtFEjJz1fetM8usF/Quf8MQzDb8/EsWoatIVHpyOarulYtDzK2r7V+iOU2mZaASJZayzpuW6PTDqlNmofuRaTki963mW6ZQMlc7ygw7To3HQiymDF7YwnVlv0g4OvT4AzDEe/1EqTkSA6LbpO2wnDbuRlahdFAbmZb9K+8tigU1FI5YlTN1djcZyyVhnuymOBp3bRATiEfyGqbJMVPSlyOez3p4srxddVkDnffPHE1jM2RAebd2FdWmO0wWxSCuAtCUfime2EQQeWfEITfJm+lfVwIyXhjHiYnQ68JtTAl1EUEbNZl8/jVU6L4Xr8tCe6DuBMC7/gz85HVD2hFyqd1kgEC4HxsFYUKz0ty4CrlUmG6aQ2Tc22VSyWtOnPbS/kuLHTqUk/jKtTvLXpaZLDxEaKXfVfoyvt61Nzwk5PylXpmCMyV5bmnZIyXwpwbA2axTghOjFVrgZHWhxqigAC216xUscKoqO6RlqVysxneOXV5QCT5s/hctrLhWXNKTvfh7uP35Y1lmp1OqaayYdqrr53DDcFOKKTEosKeMkd2u8UZtm5a6eY2ulwao7rcKTx66xhgosgsp9+f9488FyW9E6BBZHacZuxAkO/q3QcprqbVyidTkJMC6kZDYZstLH2NIQBnqWy0hFTzrSHHDs0MCJaW4fuw/Vr+et0Xcfza7jvfCSCbVhqqP8VdBymIJ9Xdp2OeI4dCRPOiAeHjhKLSzBJY6NTEQsdN2dKRusJ5IC2MOCarSz7F99oTlbjSAnS83fpuHZtyZxSp5P2DW5eR9neMeylbXRL9z0VbqNTm8OIk1VKPRWEebsNH/qDkzBuTtcappuf2zaGZtajoCjPOh1nuHA2JzqdFvsENBxoQIOioMw5WQEeTSc1fAJuqTKCxas1uzUMOOgsnLIVy3Ucfu18pZThTHVYvICO87ZiStPBpVrhgJKleVM2Tq2rwthOB01ybq9SRu06ZHuFlcKrjHDKVpb938N15y1y2qrr4AvoOHfXfnRccGREUmn0yjGelQ42wdxZJTMDJ4KOkQ68uK+dQ6hMrtY3WrWboOOGsxtDYY89zovo8JUqwYvS8ZCKFrbNabqepCNVsexQtkAHWpQ6t+hThHMqqHGl9LRsa3Cmox41Cano89Udi36WjqxWCZn/Xa+gsXkFy19ae6yNbh4V1e7bApWdjkJ1HY7ZLlrRv4fhao+4PU9H7rgeTXsBHSIis/TQ6lG3NiVYBOKx8gexVVKl9vGofeCMoXDFg7rQ7rNwDG72Mp6nA81+OFovoNM6aiotu1E2q9gTzrKRWYkjdx67UT84ciCoVdF7T3AM7DvO/RMe9STRot1XxAocm8NiHrCppqrogcQbztKSxrXwJPZIzuwW+MKBLGrbbsOVCtwIx+BqDejcfI/gNO0W86+f07H7UMlSEQpJdqMP5g9nqbR2OSJcoVPqCR+TzxuOvjzKSA61uQHHwGbbzuYO1qejfP71czr2yVMsReZ66aVsAg3HorpHpQK5dfJE6glCrBum6byIhGMJSyPv/WN0AP4gY3Wy27a9Y1pZe/tay2aNGVeKyxChoy9h6SoLnIJsdDbrurb7iZ12FtgaW5zcElvraD2hc2GxxHxIS4am0M1m28S0WjVsxj/CUs6EXxEFtVmKSDsmQ4F/eEnD6aRKGR7O5eJINLbwEddOrvGJDvhgdGO5tLoxLjTC9Qj3+UjcU4OAzseOEJHW4W1PGsbhP8WffcV+p6YPXiH8r9+00nzvsu9pOOOAZ3g1U5wBy+0NeQKs9iJCp6GReevokU/2FR8jFkbA9UgfiX7RF3AOPhcLYnScntObye/vRklrYagwRa5wTV1IgKCAhMiQ38zV0tCduiVJl2m9qBF/8pIWnFK0Y1/1wVKa9xBgllC26kBBEw89Dq7RDqFUR9RrYgXcPMNkhgITfJnMvKMor7ONoEs9e627hvKiop41GYN1Wi9gYSsyd74l8BjpLEvzTP7rbrsFBXju+mZ1P2hHqVbhymlSstzeeh0dySJVzVhDHJTLi5jIcROpMi3XHHErFD0mKXmoraxN45XODPdCBhXVdlQy31JUlSpnKrVyEJtw85be59V+YAB4hl8Ln8PdE844Sos8juO8QAc8r4qzjSdWHOqIx2vuqemlykvMTb18CiKP01texrJprXY4KI9FZaNZQSwX2XitnlUiQSlv34+zF/VCltJyNhMav2uQ5ca5vMqvzN9a01NjRIxujt3L5Q587sf96XLa3+Bm1YcfHDFElI8ptI8DXhcFrdJeEfpkfvr8JMUP6E9EyuWTE+KkchcXatZm6pbRi2r8HkTJpzW016qMlebUpvtwdl0SOO/Vy27D1es2zqORXI4v/KVSHM+Ny+LYbE+mJCBzKaCSvJCd61zfYFV1mmqLbzR9G9J3vMT1l3Jwt0VJb6iykqfTJybLp0ZmL+BrsQY9GME3ZktnPX3j86rz4fa4KXo7eH6A8HflXf/J5V+R80XI6ibnXRLobFkCnS1LoLNlCXS2LIHOliXQ2bIEOluWQGfLEuhsWQKdLUugs2UJdLYs7F3/lDdIkCBBggQJ8g35Dxs3FnU3pAQ1AAAAAElFTkSuQmCC"
          />
          <img
            class="footer-image footer-image-light"
            src="/brand/logo-simplesales-light.png"
            alt="SimpleSales"
          />
        </a>
      </div>

      <WazzupSettingsModal
        :open="isWazzupSettingsOpen"
        :users="usersFlat"
        @close="isWazzupSettingsOpen = false"
        @saved="onWazzupSettingsSaved"
      />
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import utcPlugin from "dayjs/plugin/utc"; // dependent on utc plugin
import timezonePlugin from "dayjs/plugin/timezone";
import relativeTimePlugin from "dayjs/plugin/relativeTime";
import durationPlugin from "dayjs/plugin/duration";
dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);
dayjs.extend(relativeTimePlugin);
dayjs.extend(durationPlugin);
// import 'dayjs/locale/ru'
import humanizeDuration from "humanize-duration";
//local
import DatePicker from "./components/DatePicker.vue";
import PeriodDashboard from "./components/PeriodDashboard.vue";
import WazzupSettingsModal from "./components/WazzupSettingsModal.vue";
import * as utils from "./scripts/utils.js";
import * as multiselectOptions from "./scripts/multiselect_schemes.js";
import { buildPeriodAnalytics } from "./scripts/periodAnalytics.js";
import activityApi from "./services/activityApi.js";
// import 'jquery'
import "./scripts/jquery-1.9.0.min.js";
const $ = window.$;
import "./scripts/jquery.multiselect.js";

const DEFAULT_SIMPLE_ACTIVE_PADDING_MIN = 5;
const DEFAULT_MESSAGE_PADDING_MIN = 1;
const DEFAULT_PERIOD_GOOD_PERCENT = 50;
const DEFAULT_PERIOD_WARNING_PERCENT = 20;
const DEFAULT_THEME_MODE = "dark";
const THEME_MODE_TO_UI_THEME = {
  dark: "executive",
  light: "command39",
};
const UI_THEME_TO_THEME_MODE = {
  executive: "dark",
  command: "light",
  command11: "light",
  command39: "light",
};
const isDemoProvider = activityApi.getProvider() === "demo";
const urlSearchParams = new URLSearchParams(window.location.search);
const demoPathView = window.location.pathname.match(/^\/pa-demo\/period\/?$/)
  ? "period"
  : window.location.pathname.match(/^\/pa-demo(?:\/day)?\/?$/)
    ? "day"
    : "";
const isDemoLandingPath = Boolean(demoPathView);
const requestedViewMode =
  urlSearchParams.get("view") || urlSearchParams.get("mode") || demoPathView;
const hasDateParamsInUrl = ["day", "day_from", "day_to"].some((key) =>
  urlSearchParams.has(key)
);
const hasFilterParamsInUrl = ["users", "et"].some((key) =>
  urlSearchParams.has(key)
);
const isDemoDayView = isDemoProvider && requestedViewMode === "day";
const isDemoPeriodView = isDemoProvider && requestedViewMode === "period";
const platformStorageScope = activityApi.getStorageScope();
const paramsFromCache = utils.getParamsFromLocalStorage(
  window.location.search,
  platformStorageScope
);
const getLocalSettingsStorageKey = () =>
  `PANEL_ACTIVITY_COPY${platformStorageScope}_local_panel_settings`;
const getInitialThemeMode = () => {
  const themeFromUrl = urlSearchParams.get("theme");

  if (themeFromUrl && UI_THEME_TO_THEME_MODE[themeFromUrl]) {
    return UI_THEME_TO_THEME_MODE[themeFromUrl];
  }

  if (isDemoProvider && isDemoLandingPath) {
    return "light";
  }

  try {
    const cached = JSON.parse(
      localStorage.getItem(getLocalSettingsStorageKey()) || "null"
    );
    return cached && THEME_MODE_TO_UI_THEME[cached.themeMode]
      ? cached.themeMode
      : DEFAULT_THEME_MODE;
  } catch (error) {
    return DEFAULT_THEME_MODE;
  }
};
const initialThemeMode = getInitialThemeMode();
const createDefaultParams = () => ({
  day: null,
  dayFrom: null,
  dayTo: null,
  users: null,
  eventTypes: null,
});

const mainVueApp = {
  components: {
    DatePicker,
    PeriodDashboard,
    WazzupSettingsModal,
  },
  data() {
    const today = dayjs().format("YYYY-MM-DD");
    const defaultDay = isDemoProvider
      ? dayjs().subtract(1, "day").format("YYYY-MM-DD")
      : today;
    const demoDefaultDayFrom = dayjs(defaultDay)
      .subtract(29, "day")
      .format("YYYY-MM-DD");
    const shouldUseDemoPeriodDefaults = isDemoPeriodView && !hasDateParamsInUrl;
    const shouldUseDemoDayDefaults = isDemoDayView && !hasDateParamsInUrl;
    const shouldUseDemoFilterDefaults =
      (isDemoPeriodView || isDemoDayView) && !hasFilterParamsInUrl;
    const defaultDayFrom = shouldUseDemoPeriodDefaults
      ? demoDefaultDayFrom
      : defaultDay;
    const defaultDayTo = defaultDay;
    const cachedDayFrom = shouldUseDemoPeriodDefaults || shouldUseDemoDayDefaults
      ? null
      : paramsFromCache.params.dayFrom || paramsFromCache.params.day;
    const cachedDayTo = shouldUseDemoPeriodDefaults || shouldUseDemoDayDefaults
      ? null
      : paramsFromCache.params.dayTo || paramsFromCache.params.day;
    const initialDayFrom = cachedDayFrom || defaultDayFrom;
    const initialDayTo = cachedDayTo || defaultDayTo;
    const initialUsers = shouldUseDemoFilterDefaults
      ? null
      : paramsFromCache.params.users;
    const initialEventTypes = shouldUseDemoFilterDefaults
      ? null
      : paramsFromCache.params.eventTypes;
    const initialViewMode =
      isDemoPeriodView ||
      (
        !isDemoDayView &&
        initialDayFrom &&
        initialDayTo &&
        initialDayFrom !== initialDayTo
      )
        ? "period"
        : "day";

    return {
      intervalSec: {
        data: 60 + 30,
        settings: 60 * 4,
        depsAndEventTypes: 60 * 60 * 1,
      },
      uiTheme: THEME_MODE_TO_UI_THEME[initialThemeMode],
      dataUpdateIntervalId: null,
      settingsUpdateIntervalId: null,
      filterDataUpdateIntervalId: null,
      params: {
        ...createDefaultParams(),
        ...paramsFromCache.params,
        dayFrom: initialDayFrom,
        dayTo: initialDayTo,
        day: initialViewMode === "period" ? null : initialDayFrom,
        users: initialUsers,
        eventTypes: initialEventTypes,
      },
      platformStorageScope,
      utils: utils,
      dayjs: dayjs,
      isLoadingData: false,
      loadError: "",

      usersByDeps: [],
      eventTypesRaw: [],
      settings: {},
      events: {},
      dayMessageData: null,
      workTime: {},
      periodDailyData: [],
      comparisonPeriodDailyData: [],
      periodMessageDailyData: [],

      select__dayFrom:
        initialDayFrom,
      select__dayTo:
        initialDayTo,
      select__users: initialUsers || [],
      select__eventTypes: initialEventTypes || [],
      viewMode: initialViewMode,
      periodPreset: "custom",
      includeWeekends: false,
      workingHoursMode: "schedule",
      minCallDurationSec: 0,
      isSettingsPanelOpen: false,
      isHelpPanelOpen: false,
      isWazzupSettingsOpen: false,
      localPanelSettings: {
        workStart: "09:00",
        workEnd: "18:00",
        timezone: "Europe/Moscow",
        simpleActivePaddingMin: DEFAULT_SIMPLE_ACTIVE_PADDING_MIN,
        messagePaddingMin: DEFAULT_MESSAGE_PADDING_MIN,
        periodGoodPercent: DEFAULT_PERIOD_GOOD_PERCENT,
        periodWarningPercent: DEFAULT_PERIOD_WARNING_PERCENT,
        themeMode: initialThemeMode,
      },
      timezoneOptions: [
        "Europe/Moscow",
        "Europe/Kaliningrad",
        "Europe/Samara",
        "Asia/Yekaterinburg",
        "Asia/Omsk",
        "Asia/Novosibirsk",
        "Asia/Krasnoyarsk",
        "Asia/Irkutsk",
        "Asia/Yakutsk",
        "Asia/Vladivostok",
      ],
      periodStatusFilter: null,
      focusedEmployeeId: null,
      focusedHeatmapDay: "",
      dayDrilldownContext: null,
      scaleBorders: {},
      optionsTimeHumanize: {
        forSegmentTooltip: utils.optionsTimeHumanize.ruOnlyHoursAndMinutes,
        forStatTitle: utils.optionsTimeHumanize.ruLargest3,
        // forStatLong: utils.optionsTimeHumanize.ruOnlyHours,
        // forStatLong: utils.optionsTimeHumanize.ruOnlyHoursShort,
        // forStatShort: utils.optionsTimeHumanize.ruOnlyHoursShort,
        forStat: utils.optionsTimeHumanize.ruOnlyHoursShort,
      },
    };
  },
  computed: {
    wazzupAvailable() {
      return activityApi.supportsWazzup();
    },
    visibleUsersByDeps() {
      return this.usersByDeps.filter((deps) =>
        deps.users.some((user) => this.select__users.includes(user.id))
      );
    },
    normalizedPeriod() {
      const from = dayjs(this.select__dayFrom).isAfter(dayjs(this.select__dayTo))
        ? this.select__dayTo
        : this.select__dayFrom;
      const to = dayjs(this.select__dayFrom).isAfter(dayjs(this.select__dayTo))
        ? this.select__dayFrom
        : this.select__dayTo;

      return { from, to };
    },
    selectedPeriodDays() {
      const days = [];
      let currentDay = dayjs(this.normalizedPeriod.from);
      const lastDay = dayjs(this.normalizedPeriod.to);

      while (
        currentDay.isBefore(lastDay) ||
        currentDay.format("YYYY-MM-DD") === lastDay.format("YYYY-MM-DD")
      ) {
        days.push(currentDay.format("YYYY-MM-DD"));
        currentDay = currentDay.add(1, "day");
      }

      return days;
    },
    comparisonPeriodDays() {
      const periodLength = this.selectedPeriodDays.length || 1;
      const comparisonEnd = dayjs(this.normalizedPeriod.from).subtract(1, "day");
      const comparisonStart = comparisonEnd.subtract(periodLength - 1, "day");
      const days = [];
      let currentDay = comparisonStart;

      while (
        currentDay.isBefore(comparisonEnd) ||
        currentDay.format("YYYY-MM-DD") === comparisonEnd.format("YYYY-MM-DD")
      ) {
        days.push(currentDay.format("YYYY-MM-DD"));
        currentDay = currentDay.add(1, "day");
      }

      return days;
    },
    isPeriodMode() {
      return this.viewMode === "period";
    },
    isCommandTheme() {
      return ["command", "command11", "command39"].includes(this.uiTheme);
    },
    formattedWorkingHours() {
      if (!this.settings.workingHours) {
        return "";
      }

      return this.settings.workingHours
        .split("-")
        .map((time) => (time.includes(":") ? time : `${time}:00`))
        .join(" - ");
    },
    localWorkingHours() {
      return `${this.localPanelSettings.workStart}-${this.localPanelSettings.workEnd}`;
    },
    formattedPanelWorkingHours() {
      return `${this.localPanelSettings.workStart} - ${this.localPanelSettings.workEnd}`;
    },
    panelTimezone() {
      return this.localPanelSettings.timezone || this.settings.timezone || "Europe/Moscow";
    },
    availableTimezoneOptions() {
      return Array.from(
        new Set([
          this.localPanelSettings.timezone,
          this.settings.timezone,
          ...this.timezoneOptions,
        ].filter(Boolean))
      );
    },
    settingsSummaryTitle() {
      return [
        `График панели: ${this.formattedPanelWorkingHours}`,
        `Часовой пояс: ${this.panelTimezone}`,
        `CRM: +/- ${this.localPanelSettings.simpleActivePaddingMin} мин`,
        `Сообщения: +/- ${this.localPanelSettings.messagePaddingMin} мин`,
        this.localPanelSettings.themeMode === "light"
          ? "Тема: светлая 39"
          : "Тема: оформление 3.0",
        this.formattedWorkingHours
          ? `На бэке: ${this.formattedWorkingHours}, ${this.settings.timezone}`
          : "",
      ]
        .filter(Boolean)
        .join("\n");
    },
    periodThresholds() {
      const warning = this.clampPercent(
        this.localPanelSettings.periodWarningPercent,
        DEFAULT_PERIOD_WARNING_PERCENT
      );
      const good = Math.max(
        warning,
        this.clampPercent(
          this.localPanelSettings.periodGoodPercent,
          DEFAULT_PERIOD_GOOD_PERCENT
        )
      );

      return {
        good,
        warning,
        risk: warning,
        critical: warning,
      };
    },
    periodCriticalLabel() {
      return `< ${this.periodThresholds.warning}%`;
    },
    MAP_EVENT_TYPES_ID_TO_TITLE() {
      return Object.fromEntries(
        this.eventTypes.map(({ id, title }) => [id, title])
      );
    },
    MAP_EVENT_TYPES_ID_TO_EN() {
      return Object.fromEntries(
        this.eventTypes.map(({ id, titleEN }) => [id, titleEN])
      );
    },
    eventTypes() {
      // фильтрую список событий, убирая из него Рабочее время
      return this.eventTypesRaw
        .filter(({ typeId }) => +typeId !== -3)
        .map(({ typeId, titleRU, titleEN }) => ({
          id: typeId,
          title: titleRU,
          titleEN,
        }))
        .sort((a, b) =>
          String(a.title || "").localeCompare(String(b.title || ""), "ru", {
            sensitivity: "base",
          })
        );
    },
    messageTypeIds() {
      const normalizeTitle = (value = "") =>
        String(value)
          .trim()
          .toLowerCase()
          .replace(/ё/g, "е");
      const isMessageType = ({ title, titleEN }) => {
        const combinedTitle = `${normalizeTitle(title)} ${normalizeTitle(titleEN)}`;

        return (
          /(сообщ|message|переписк|correspondence)/i.test(combinedTitle) &&
          /(вход|исход|incoming|outgoing|переписк|correspondence)/i.test(
            combinedTitle
          )
        );
      };

      return this.eventTypes
        .filter(isMessageType)
        .map(({ id }) => id);
    },
    isOnlyMessageEventTypesSelected() {
      if (!this.select__eventTypes.length || !this.messageTypeIds.length) {
        return false;
      }

      return this.select__eventTypes.every((typeId) =>
        this.messageTypeIds.includes(Number(typeId))
      );
    },
    NOT_UNION_EVENT_TYPES_KEYS() {
      // фильтрую список событий, убирая из него Рабочее время
      return Object.keys(this.settings.NOT_UNION_EVENT_TYPES_MAP || {})
        .filter((id) => +id !== -3)
        .map(Number);
    },
    NOT_UNION_EXCLUDE_CUSTOM_EVENT_TYPES_KEYS() {
      return this.NOT_UNION_EVENT_TYPES_KEYS.filter((id) => id > 0);
    },
    scaleBordersForRequest() {
      console.log("#cmomputed @scaleBordersForRequest");

      const scaleBordersForRequest = this.workingHoursMode === "full_day"
        ? this.createTimeBorders(
            this.normalizedPeriod.from,
            "00-24",
            this.panelTimezone
          )
        : this.createTimeBorders(
            this.normalizedPeriod.from,
            this.localWorkingHours,
            this.panelTimezone
          );

      return scaleBordersForRequest;
    },
    beginScale() {
      const beginScale = {};

      for (const managerId in this.events) {
        beginScale[managerId] = this.workTime[managerId].fromTimeUnix;
      }

      return beginScale;
    },
    endScale() {
      const endScale = {};

      for (const managerId in this.events) {
        endScale[managerId] = this.workTime[managerId].toTimeUnix;
      }

      return endScale;
    },
    beginScale00() {
      const beginScale00 = {};

      for (const managerId in this.events) {
        beginScale00[managerId] =
          dayjs
            .unix(this.beginScale[managerId])
            .startOf("hours")
            .unix() === dayjs.unix(this.beginScale[managerId]).unix()
            ? this.beginScale[managerId]
            : dayjs
                .unix(this.beginScale[managerId])
                .startOf("hour")
                .add(1, "h")
                .unix();
      }

      return beginScale00;
    },
    endScale00() {
      const endScale00 = {};

      for (const managerId in this.events) {
        endScale00[managerId] = dayjs
          .unix(this.endScale[managerId])
          .startOf("hour")
          .unix();
      }

      return endScale00;
    },
    scaleWidth() {
      const scaleWidth = {};

      for (const managerId in this.events) {
        scaleWidth[managerId] =
          this.endScale[managerId] - this.beginScale[managerId];
      }

      return scaleWidth;
    },
    scaleWidthInHours() {
      const scaleWidthInHours = {};

      for (const managerId in this.events) {
        scaleWidthInHours[managerId] = dayjs
          .duration(this.scaleWidth[managerId], "seconds")
          .asHours();
      }

      return scaleWidthInHours;
    },
    scaleMarks() {
      const scaleMarks = {};

      for (const managerId in this.events) {
        scaleMarks[managerId] = [];

        const scaleWidth00InHours = dayjs
          .duration(
            this.endScale00[managerId] - this.beginScale00[managerId],
            "seconds"
          )
          .asHours();

        for (let h = 0; h <= scaleWidth00InHours; h++) {
          const currentTime = dayjs
            .unix(this.beginScale00[managerId])
            .add(h, "hour");

          const offsetStartHours = dayjs
            .duration(
              currentTime.unix() - this.beginScale[managerId],
              "seconds"
            )
            .asHours();

          scaleMarks[managerId].push({
            title: dayjs.tz(currentTime, this.panelTimezone).format("H:mm"),
            style: {
              left: `${(offsetStartHours / this.scaleWidthInHours[managerId]) *
                100}%`,
            },
            class: {
              isFirstScale: currentTime.unix() == this.beginScale00[managerId],
              isLastScale: currentTime.unix() == this.endScale00[managerId],
            },
          });
        }
      }

      return scaleMarks;
    },
    segments() {
      const segments = {};
      for (const managerId in this.events) {
        segments[managerId] = this.events[managerId]
          .filter((event) => !this.isMessageBackedEvent(event, managerId))
          .map((event) => {
            const group = this.getEventGroup(event);
            const eventStartAt = event.originalStartAt || event.startAt;
            const segmentStart =
              group === "simple_active"
                ? Math.max(
                    eventStartAt - this.getSimpleActivePaddingSec(),
                    this.beginScale[managerId]
                  )
                : event.startAt;
            const segmentDuration =
              group === "simple_active"
                ? Math.max(
                    Math.min(
                      eventStartAt + this.getSimpleActivePaddingSec(),
                      this.endScale[managerId]
                    ) - segmentStart,
                    0
                  )
                : event.originalDuration !== undefined
                  ? event.originalDuration
                  : event.duration;

            return {
              ...event,
              startAt: segmentStart,
              duration: segmentDuration,
              left:
                ((segmentStart - this.beginScale[managerId]) /
                  this.scaleWidth[managerId]) *
                100,
              width: (segmentDuration / this.scaleWidth[managerId]) * 100,
              group,
            };
          });
      }
      return segments;
    },
    messageSegments() {
      const segments = {};
      const messageEventsByUser =
        (this.dayMessageData && this.dayMessageData.events) || {};

      for (const managerId in this.events) {
        const scaleWidth = this.scaleWidth[managerId] || 1;

        const rawSegments = (messageEventsByUser[managerId] || [])
          .map((event) => {
            const startAt = Math.max(event.startAt, this.beginScale[managerId]);
            const endAt = Math.min(
              event.startAt + event.duration,
              this.endScale[managerId]
            );
            const duration = Math.max(endAt - startAt, 0);
            const messageAt =
              event.messageAt ||
              event.originalStartAt ||
              Math.floor(startAt + duration / 2);

            return {
              ...event,
              messageAt,
              startAt,
              duration,
              endAt: startAt + duration,
              messageCount: this.getMessageEventCount(event),
              left:
                ((startAt - this.beginScale[managerId]) / scaleWidth) * 100,
              width: (duration / scaleWidth) * 100,
              group: "messages",
            };
          })
          .filter((event) => event.duration > 0)
          .sort((a, b) => a.startAt - b.startAt);

        segments[managerId] = this.mergeMessageSegments(rawSegments).map(
          (segment) => {
            const duration = Math.max(segment.endAt - segment.startAt, 0);

            return {
              ...segment,
              duration,
              left:
                ((segment.startAt - this.beginScale[managerId]) /
                  scaleWidth) *
                100,
              width: (duration / scaleWidth) * 100,
            };
          }
        );
      }

      return segments;
    },
    activeSegments() {
      const segments = {};

      for (const managerId in this.events) {
        segments[managerId] = [
          ...(this.segments[managerId] || []),
          ...(this.messageSegments[managerId] || []),
        ].sort((a, b) => a.startAt - b.startAt);
      }

      return segments;
    },
    messageEventTimesByUser() {
      const result = {};
      const messageEventsByUser =
        (this.dayMessageData && this.dayMessageData.events) || {};

      for (const managerId in this.events) {
        result[managerId] = new Set(
          (messageEventsByUser[managerId] || []).map(
            (event) => event.originalStartAt || event.messageAt || event.startAt
          )
        );
      }

      return result;
    },
    messageEventRangesByUser() {
      const result = {};
      const messageEventsByUser =
        (this.dayMessageData && this.dayMessageData.events) || {};

      for (const managerId in this.events) {
        result[managerId] = (messageEventsByUser[managerId] || []).map(
          (event) => ({
            startAt: event.startAt,
            endAt: event.startAt + event.duration,
          })
        );
      }

      return result;
    },
    segmentsInactive() {
      const segments = {};

      for (const managerId in this.events) {
        let start = this.beginScale[managerId];
        const end = this.getNowEnd(managerId);
        const managerEvents = this.activeSegments[managerId] || [];
        const res = [];

        for (let i = 0; i < managerEvents.length; i++) {
          const currentActive = managerEvents[i];
          const durationNewRed = currentActive.startAt - start;

          if (durationNewRed > 0) {
            res.push({
              startAt: start,
              duration: durationNewRed,
              left:
                ((start - this.beginScale[managerId]) /
                  this.scaleWidth[managerId]) *
                100,
              width: (durationNewRed / this.scaleWidth[managerId]) * 100,
              group: "inactive",
            });
          }

          start = Math.max(start, currentActive.startAt + currentActive.duration);
        }

        const lastActiveSegment = managerEvents[managerEvents.length - 1] || {
          startAt: start,
          duration: 0,
        };
        const durationNewRed =
          end - (lastActiveSegment.startAt + lastActiveSegment.duration);

        if (durationNewRed > 0) {
          res.push({
            startAt: lastActiveSegment.startAt + lastActiveSegment.duration,
            duration: durationNewRed,
            left:
              ((lastActiveSegment.startAt +
                lastActiveSegment.duration -
                this.beginScale[managerId]) /
                this.scaleWidth[managerId]) *
              100,
            width: ((durationNewRed + 1) / this.scaleWidth[managerId]) * 100,
          });
        }
        segments[managerId] = res;
      }
      return segments;
    },
    allUsersIds() {
      return this.getAllUsersIds(this.usersByDeps);
    },
    usersFlat() {
      return this.usersByDeps.flatMap((deps) =>
        deps.users.map((user) => ({
          id: user.id,
          name: user.name,
          departmentId: deps.id,
          departmentName: deps.name,
        }))
      );
    },
    allEventTypesIds() {
      return this.eventTypes.map((e) => e.id);
    },
    segmentsSumRedDuration() {
      return this.getSegmentsSumDuration(this.segmentsInactive);
    },
    segmentsSumCallDuration() {
      return this.getSegmentsSumDuration(
        this.segments,
        this.NOT_UNION_EXCLUDE_CUSTOM_EVENT_TYPES_KEYS
      );
    },
    segmentsMessageCount() {
      const count = {};

      for (const managerId in this.events) {
        count[managerId] = (this.messageSegments[managerId] || []).reduce(
          (sum, segment) => sum + (Number(segment.messageCount) || 1),
          0
        );
      }

      return count;
    },
    segmentsSumMeetingDuration() {
      return this.getSegmentsSumDuration(this.segments, [-1]);
    },
    segmentsSumLunchTimeDuration() {
      return this.getSegmentsSumDuration(this.segments, [-2]);
    },
    segmentsSumDuration() {
      return this.getSegmentsSumDuration(this.activeSegments);
    },
    periodTimeline() {
      const timeline = {};

      for (const managerId of this.allUsersIds) {
        timeline[managerId] = this.periodDailyData.map((dayData) =>
          this.createPeriodTimelineDay(dayData, managerId)
        );
      }

      return timeline;
    },
    periodUserStats() {
      const stats = {};

      for (const managerId of this.allUsersIds) {
        stats[managerId] = {
          active: 0,
          inactive: 0,
          visible: 0,
          calls: 0,
          meeting: 0,
          lunchTime: 0,
        };

        for (const dayData of this.periodDailyData) {
          const timelineDay = this.createPeriodTimelineDay(dayData, managerId);
          const events = timelineDay.segments;

          stats[managerId].visible += timelineDay.visibleDuration;
          stats[managerId].active += timelineDay.activeDuration;
          stats[managerId].calls += this.getEventsUnionDuration(
            events.filter((event) => this.getEventGroup(event) === "calls")
          );
          stats[managerId].meeting += this.getEventsUnionDuration(
            events.filter((event) => event.typeId === -1)
          );
          stats[managerId].lunchTime += this.getEventsUnionDuration(
            events.filter((event) => event.typeId === -2)
          );
        }

        stats[managerId].inactive = Math.max(
          stats[managerId].visible - stats[managerId].active,
          0
        );
      }

      return stats;
    },
    activeDepartmentUserIds() {
      return this.select__users;
    },
    periodDashboard() {
      return buildPeriodAnalytics({
        currentDays: this.selectedPeriodDays,
        previousDays: this.comparisonPeriodDays,
        periodDailyData: this.periodDailyData,
        comparisonDailyData: this.comparisonPeriodDailyData,
        periodMessageDailyData: this.periodMessageDailyData,
        users: this.usersFlat,
        visibleUserIds: this.activeDepartmentUserIds,
        includeWeekends: this.includeWeekends,
        getEventGroup: this.getEventGroup,
        getNowEndForWorkTime: this.getNowEndForWorkTime,
        messageTypeIds: this.messageTypeIds,
        minCallDurationSec: this.minCallDurationSec,
        thresholds: this.periodThresholds,
      });
    },
    filteredHeatmapRows() {
      let rows = this.periodDashboard.userRows;

      if (this.periodStatusFilter) {
        rows = rows.filter((row) => row.status === this.periodStatusFilter);
      }

      if (this.focusedEmployeeId) {
        rows = rows.filter((row) => row.id === this.focusedEmployeeId);
      }

      return rows;
    },
    filteredRanking() {
      const allowedIds = this.filteredHeatmapRows.map((row) => row.id);
      return this.periodDashboard.ranking.filter((row) => allowedIds.includes(row.id));
    },
    selectedEmployeeCard() {
      if (!this.focusedEmployeeId) {
        return null;
      }

      return (
        this.periodDashboard.userRows.find(
          (row) => row.id === this.focusedEmployeeId
        ) || null
      );
    },
  },
  methods: {
    async setViewMode(mode) {
      if (this.viewMode === mode) {
        return;
      }

      if (mode === "period" && this.dayDrilldownContext) {
        await this.returnToPeriod();
        return;
      }

      this.viewMode = mode;
      this.clearPeriodFocus();
      this.isSettingsPanelOpen = false;
      this.isHelpPanelOpen = false;

      if (mode === "day") {
        this.select__dayTo = this.select__dayFrom;
      } else if (this.select__dayFrom === this.select__dayTo) {
        await this.applyPeriodPreset("week");
        return;
      }

      this.syncPeriodParams();
      this.createIntervalUpdate();
      this.requestData();
    },
    async applyPeriodPreset(preset) {
      this.periodPreset = preset;
      const anchor = dayjs(this.select__dayTo || this.select__dayFrom);

      if (preset === "week") {
        this.select__dayTo = anchor.format("YYYY-MM-DD");
        this.select__dayFrom = anchor.subtract(6, "day").format("YYYY-MM-DD");
      } else if (preset === "month") {
        this.select__dayTo = anchor.format("YYYY-MM-DD");
        this.select__dayFrom = anchor.subtract(29, "day").format("YYYY-MM-DD");
      }

      if (this.viewMode !== "period") {
        this.viewMode = "period";
      }

      this.syncPeriodParams();
      this.createIntervalUpdate();
      await this.requestData();
    },
    setWorkingHoursMode(mode) {
      this.workingHoursMode = mode;
    },
    focusEmployee(userId) {
      this.focusedEmployeeId = this.focusedEmployeeId === userId ? null : userId;
    },
    focusHeatmapDay(day) {
      this.focusedHeatmapDay = this.focusedHeatmapDay === day ? "" : day;
    },
    clearPeriodFocus() {
      this.periodStatusFilter = null;
      this.focusedEmployeeId = null;
      this.focusedHeatmapDay = "";
    },
    applyInsight(insight) {
      this.periodStatusFilter = null;
      this.focusedEmployeeId =
        insight.userIds && insight.userIds.length === 1 ? insight.userIds[0] : null;
      this.focusedHeatmapDay =
        insight.days && insight.days.length === 1 ? insight.days[0] : "";
    },
    async openDayDrilldown({ userId, day }) {
      this.dayDrilldownContext = {
        dayFrom: this.select__dayFrom,
        dayTo: this.select__dayTo,
        users: this.select__users.length
          ? [...this.select__users]
          : [...this.allUsersIds],
        eventTypes: [...this.select__eventTypes],
        periodPreset: this.periodPreset,
        includeWeekends: this.includeWeekends,
        workingHoursMode: this.workingHoursMode,
        minCallDurationSec: this.minCallDurationSec,
        periodStatusFilter: this.periodStatusFilter,
        focusedEmployeeId: this.focusedEmployeeId,
        focusedHeatmapDay: this.focusedHeatmapDay,
        scrollY: window.scrollY,
      };
      this.viewMode = "day";
      this.select__dayFrom = day;
      this.select__dayTo = day;
      this.select__users = [userId];
      this.syncUsersMultiselect();
      this.syncPeriodParams();
      this.createIntervalUpdate();
      await this.$nextTick();
      await this.requestData();
    },
    async returnToPeriod() {
      const context = this.dayDrilldownContext;

      if (!context) {
        return;
      }

      this.dayDrilldownContext = null;
      this.viewMode = "period";
      this.select__dayFrom = context.dayFrom;
      this.select__dayTo = context.dayTo;
      this.select__users = [...context.users];
      this.select__eventTypes = [...context.eventTypes];
      this.periodPreset = context.periodPreset;
      this.includeWeekends = context.includeWeekends;
      this.workingHoursMode = context.workingHoursMode;
      this.minCallDurationSec = context.minCallDurationSec;
      this.periodStatusFilter = context.periodStatusFilter;
      this.focusedEmployeeId = context.focusedEmployeeId;
      this.focusedHeatmapDay = context.focusedHeatmapDay;
      this.isSettingsPanelOpen = false;
      this.isHelpPanelOpen = false;
      this.syncUsersMultiselect();
      this.syncEventTypesMultiselect();
      this.syncPeriodParams();
      this.createIntervalUpdate();
      await this.$nextTick();
      await this.requestData();
      await this.$nextTick();
      window.scrollTo({ top: context.scrollY || 0, behavior: "smooth" });
    },
    getNowEnd(managerId) {
      const now = dayjs().unix();
      const end =
        now < this.endScale[managerId] ? now : this.endScale[managerId];
      return end;
    },
    getNowEndForWorkTime(day, workTime) {
      const now = dayjs().unix();
      const isToday = this.isDateToday(day);

      if (isToday && now < workTime.toTimeUnix) {
        return now;
      }

      return workTime.toTimeUnix;
    },
    getSimpleActivePaddingSec() {
      const minutes = Number(this.localPanelSettings.simpleActivePaddingMin);
      return Math.max(Number.isFinite(minutes) ? minutes : DEFAULT_SIMPLE_ACTIVE_PADDING_MIN, 0) * 60;
    },
    getMessagePaddingSec() {
      const minutes = Number(this.localPanelSettings.messagePaddingMin);
      return Math.max(Number.isFinite(minutes) ? minutes : DEFAULT_MESSAGE_PADDING_MIN, 0) * 60;
    },
    normalizeWorkingHourPart(value, fallback) {
      if (!value) {
        return fallback;
      }

      const time = String(value).trim();

      if (/^\d{1,2}$/.test(time)) {
        return `${time.padStart(2, "0")}:00`;
      }

      if (/^\d{1,2}:\d{2}$/.test(time)) {
        const [hours, minutes] = time.split(":");
        return `${hours.padStart(2, "0")}:${minutes}`;
      }

      return fallback;
    },
    parseWorkingHours(workingHours = "09-18") {
      const [from = "09", to = "18"] = String(workingHours).split("-");

      return {
        workStart: this.normalizeWorkingHourPart(from, "09:00"),
        workEnd: this.normalizeWorkingHourPart(to, "18:00"),
      };
    },
    clampPercent(value, fallback) {
      const percent = Number(value);

      if (!Number.isFinite(percent)) {
        return fallback;
      }

      return Math.max(0, Math.min(100, Math.round(percent)));
    },
    normalizePeriodThresholdSettings() {
      const warning = this.clampPercent(
        this.localPanelSettings.periodWarningPercent,
        DEFAULT_PERIOD_WARNING_PERCENT
      );
      const good = Math.max(
        warning,
        this.clampPercent(
          this.localPanelSettings.periodGoodPercent,
          DEFAULT_PERIOD_GOOD_PERCENT
        )
      );

      this.localPanelSettings.periodWarningPercent = warning;
      this.localPanelSettings.periodGoodPercent = good;
    },
    getLocalSettingsStorageKey() {
      return getLocalSettingsStorageKey();
    },
    applyPanelTheme(themeMode = this.localPanelSettings.themeMode) {
      const normalizedThemeMode = THEME_MODE_TO_UI_THEME[themeMode]
        ? themeMode
        : DEFAULT_THEME_MODE;

      this.localPanelSettings.themeMode = normalizedThemeMode;
      this.uiTheme = THEME_MODE_TO_UI_THEME[normalizedThemeMode];
      this.saveLocalPanelSettings();
    },
    syncUsersMultiselect() {
      this.$nextTick(() => {
        const $users = $(".tabs-users");

        if (!$users.length || !$users.data("plugin_multiselect")) {
          return;
        }

        $users.val(this.select__users);
        $users.multiselect("reload");
        this.createTitleMultiselect();
      });
    },
    syncEventTypesMultiselect() {
      this.$nextTick(() => {
        const $eventTypes = $(".tabs-event_types");

        if (!$eventTypes.length || !$eventTypes.data("plugin_multiselect")) {
          return;
        }

        $eventTypes.val(this.select__eventTypes);
        $eventTypes.multiselect("reload");
        this.createTitleMultiselect();
      });
    },
    toggleSettingsPanel() {
      this.isHelpPanelOpen = false;
      this.isSettingsPanelOpen = !this.isSettingsPanelOpen;
    },
    openWazzupSettings() {
      this.isSettingsPanelOpen = false;
      this.isWazzupSettingsOpen = true;
    },
    async onWazzupSettingsSaved() {
      await this.requestData();
    },
    toggleHelpPanel() {
      this.isSettingsPanelOpen = false;
      this.isHelpPanelOpen = !this.isHelpPanelOpen;
    },
    closeSettingsOnOutsideClick(event) {
      if (
        this.isSettingsPanelOpen &&
        !event.target.closest(".tabs-local-settings")
      ) {
        this.isSettingsPanelOpen = false;
      }

      if (
        this.isHelpPanelOpen &&
        !event.target.closest(".tabs-help")
      ) {
        this.isHelpPanelOpen = false;
      }
    },
    seedLocalPanelSettings({ force = false } = {}) {
      const cachedRaw = localStorage.getItem(this.getLocalSettingsStorageKey());
      let cached = null;

      try {
        cached = cachedRaw ? JSON.parse(cachedRaw) : null;
      } catch (error) {
        cached = null;
      }

      const backendWorkingHours = this.parseWorkingHours(
        this.settings.workingHours || "09-18"
      );

      if (cached && !force) {
        this.localPanelSettings = {
          ...this.localPanelSettings,
          ...cached,
          simpleActivePaddingMin:
            cached.simpleActivePaddingMin === undefined
              ? DEFAULT_SIMPLE_ACTIVE_PADDING_MIN
              : cached.simpleActivePaddingMin,
          messagePaddingMin:
            cached.messagePaddingMin === undefined
              ? DEFAULT_MESSAGE_PADDING_MIN
              : cached.messagePaddingMin,
          periodGoodPercent:
            cached.periodGoodPercent === undefined
              ? DEFAULT_PERIOD_GOOD_PERCENT
              : cached.periodGoodPercent,
          periodWarningPercent:
            cached.periodWarningPercent === undefined
              ? DEFAULT_PERIOD_WARNING_PERCENT
              : cached.periodWarningPercent,
          themeMode: THEME_MODE_TO_UI_THEME[cached.themeMode]
            ? cached.themeMode
            : initialThemeMode,
        };
        this.normalizePeriodThresholdSettings();
        this.applyPanelTheme();
        return;
      }

      this.localPanelSettings = {
        ...this.localPanelSettings,
        ...backendWorkingHours,
        timezone: this.settings.timezone || "Europe/Moscow",
        simpleActivePaddingMin:
          this.settings.maxMinutesBetweenEventsCountAsWork ||
          DEFAULT_SIMPLE_ACTIVE_PADDING_MIN,
        messagePaddingMin: DEFAULT_MESSAGE_PADDING_MIN,
        periodGoodPercent: DEFAULT_PERIOD_GOOD_PERCENT,
        periodWarningPercent: DEFAULT_PERIOD_WARNING_PERCENT,
        themeMode: initialThemeMode,
      };
      this.applyPanelTheme();
      this.saveLocalPanelSettings();
    },
    saveLocalPanelSettings() {
      localStorage.setItem(
        this.getLocalSettingsStorageKey(),
        JSON.stringify(this.localPanelSettings)
      );
    },
    async applyLocalPanelSettings() {
      this.normalizePeriodThresholdSettings();
      this.saveLocalPanelSettings();
      this.isSettingsPanelOpen = false;
      await this.requestData();
    },
    async resetLocalPanelSettings() {
      localStorage.removeItem(this.getLocalSettingsStorageKey());
      this.seedLocalPanelSettings({ force: true });
      await this.requestData();
    },
    mergeMessageSegments(rawSegments) {
      const merged = [];

      for (const segment of rawSegments) {
        const last = merged[merged.length - 1];

        if (!last || segment.startAt > last.endAt) {
          merged.push({
            ...segment,
            messageCount: this.normalizeMessageCount(segment.messageCount, 1),
          });
          continue;
        }

        last.endAt = Math.max(last.endAt, segment.endAt);
        last.duration = Math.max(last.endAt - last.startAt, 0);
        last.messageCount += this.normalizeMessageCount(
          segment.messageCount,
          1
        );
        last.messageAt = Math.min(
          last.messageAt || segment.messageAt,
          segment.messageAt
        );
      }

      return merged;
    },
    normalizeMessageCount(value, fallback = 1) {
      if (value !== undefined && value !== null && value !== "") {
        const count = Number(value);

        return Number.isFinite(count) ? Math.max(0, count) : fallback;
      }

      return fallback;
    },
    getMessageEventCount(event) {
      if (event.messageCount !== undefined || event.count !== undefined) {
        return this.normalizeMessageCount(
          event.messageCount !== undefined ? event.messageCount : event.count
        );
      }

      if (Number(event.typeId) === 0) {
        const sourceDuration = Number(event.originalDuration || event.duration);

        return sourceDuration > 60 ? 2 : 1;
      }

      return 1;
    },
    getMessageSegmentType(segment) {
      const count = Number(segment.messageCount) || 1;
      const noun = utils.declOfNum(count, [
        "сообщение",
        "сообщения",
        "сообщений",
      ]);

      return `Сообщения: ${count} ${noun}`;
    },
    getNowWidthScale(managerId) {
      return this.getNowEnd(managerId) - this.beginScale[managerId];
    },
    getEventGroup(event) {
      if (!this.NOT_UNION_EVENT_TYPES_KEYS.includes(Number(event.typeId))) {
        return "simple_active";
      }

      return +event.typeId < 0
        ? this.MAP_EVENT_TYPES_ID_TO_EN[Number(event.typeId)]
        : "calls";
    },
    normalizeEvent(event) {
      return {
        ...event,
        typeId: Number(event.typeId),
        originalStartAt: event.originalStartAt || event.startAt,
        originalDuration:
          event.originalDuration !== undefined
            ? event.originalDuration
            : event.duration,
      };
    },
    isMessageBackedEvent(event, managerId) {
      if (this.messageTypeIds.includes(Number(event.typeId))) {
        return true;
      }

      if (Number(event.typeId) !== 0) {
        return false;
      }

      const eventStartAt = event.originalStartAt || event.messageAt || event.startAt;
      const eventEndAt =
        eventStartAt + (event.originalDuration || event.duration || 0);
      const messageRanges = this.messageEventRangesByUser[managerId] || [];

      if (
        messageRanges.some(
          (range) => range.startAt < eventEndAt && range.endAt > eventStartAt
        )
      ) {
        return true;
      }

      const messageTimes = this.messageEventTimesByUser[managerId];

      if (!messageTimes || !messageTimes.size) {
        return false;
      }

      return Array.from(messageTimes).some(
        (messageAt) => messageAt >= eventStartAt && messageAt <= eventEndAt
      );
    },
    getPeriodUserStats(userId) {
      return (
        this.periodUserStats[userId] || {
          active: 0,
          inactive: 0,
          visible: 0,
          calls: 0,
          meeting: 0,
          lunchTime: 0,
        }
      );
    },
    getDayWord(count) {
      return utils.declOfNum(count, ["день", "дня", "дней"]);
    },
    formatPeriodDay(dayStr) {
      return new Intl.DateTimeFormat("ru-RU", {
        month: "long",
        day: "numeric",
      }).format(new Date(dayjs(dayStr).valueOf()));
    },
    formatPeriodDayShort(dayStr) {
      return new Intl.DateTimeFormat("ru-RU", {
        month: "long",
        day: "numeric",
      }).format(new Date(dayjs(dayStr).valueOf()));
    },
    getPeriodDayTitle(dayData) {
      return [
        this.formatPeriodDay(dayData.day),
        `Активен: ${this.getFormattedTimeDuration(
          dayData.activeDuration,
          this.optionsTimeHumanize.forStatTitle
        )}`,
        `Неактивен: ${this.getFormattedTimeDuration(
          Math.max(dayData.visibleDuration - dayData.activeDuration, 0),
          this.optionsTimeHumanize.forStatTitle
        )}`,
      ].join("\n");
    },
    shouldShowPeriodTick(index) {
      const totalDays = this.selectedPeriodDays.length;

      if (index === 0 || index === totalDays - 1) {
        return true;
      }

      if (totalDays <= 7) {
        return true;
      }

      if (totalDays <= 14) {
        return index % 2 === 0;
      }

      if (totalDays <= 31) {
        return index % 4 === 0;
      }

      return index % 7 === 0;
    },
    async onDateSelection({ from, to }) {
      const nextTo = this.viewMode === "period" ? to : from;

      if (from === this.select__dayFrom && nextTo === this.select__dayTo) {
        return;
      }

      this.select__dayFrom = from;
      this.select__dayTo = nextTo;
      this.periodPreset = "custom";
      this.syncPeriodParams();
      this.createIntervalUpdate();
      await this.$nextTick();
      await this.requestData();
    },
    isDateToday(dateStr) {
      return (
        dayjs(dateStr).format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD")
      );
    },
    isDateYesterday(dateStr) {
      return (
        dayjs(dateStr).format("YYYY-MM-DD") ===
        dayjs()
          .subtract(1, "day")
          .format("YYYY-MM-DD")
      );
    },
    getAllUsersIds(usersByDeps) {
      return usersByDeps.flatMap((deps) => deps.users.map((user) => user.id));
    },
    getFormattedTimeDuration(segmentDuration, optionsTimeHumanize) {
      // return dayjs.duration(segmentDuration, 'seconds').format("H:m:s");
      // return dayjs.duration(segmentDuration, 'seconds').humanize()
      // return dayjs.duration(segmentDuration, 'seconds').locale('ru').humanize()
      return humanizeDuration.humanizer(optionsTimeHumanize)(
        segmentDuration * 1000
      );
    },
    formatMinutesDuration(seconds = 0) {
      return `${Math.round(seconds / 60)} мин`;
    },
    getSegmentsSumDuration(segments, queryTypeIds) {
      const segmentsSum = {};
      for (const managerId in segments) {
        const filteredSegments = segments[managerId].filter(
          (segment) => !queryTypeIds || queryTypeIds.includes(Number(segment.typeId))
        );

        segmentsSum[managerId] = this.getEventsUnionDuration(filteredSegments);
      }
      return segmentsSum;
    },
    getEventsUnionDuration(events) {
      const intervals = events
        .map((event) => ({
          startAt: event.startAt,
          endAt: event.startAt + event.duration,
        }))
        .filter((interval) => interval.endAt > interval.startAt)
        .sort((a, b) => a.startAt - b.startAt);

      if (!intervals.length) {
        return 0;
      }

      let duration = 0;
      let currentStart = intervals[0].startAt;
      let currentEnd = intervals[0].endAt;

      for (let i = 1; i < intervals.length; i++) {
        const interval = intervals[i];

        if (interval.startAt <= currentEnd) {
          currentEnd = Math.max(currentEnd, interval.endAt);
          continue;
        }

        duration += currentEnd - currentStart;
        currentStart = interval.startAt;
        currentEnd = interval.endAt;
      }

      duration += currentEnd - currentStart;
      return duration;
    },
    isArrayEqual(arrA, arrB) {
      return (
        arrA
          .slice()
          .sort()
          .join() ===
        arrB
          .slice()
          .sort()
          .join()
      );
    },
    getTypeTitleById(typeId) {
      return this.NOT_UNION_EVENT_TYPES_KEYS.includes(Number(typeId))
        ? this.MAP_EVENT_TYPES_ID_TO_TITLE[Number(typeId)]
        : null;
    },
    getMinutesDayOffset(workingHoursString) {
      const [fromTimeStr, toTimeStr] = workingHoursString.split("-");
      const [fromTimeH = 0, fromTimeM = 0] = fromTimeStr.split(":");
      const [toTimeH = 0, toTimeM = 0] = toTimeStr.split(":");

      return {
        from: dayjs
          .duration({ hours: fromTimeH, minutes: fromTimeM })
          .asMinutes(),
        to: dayjs.duration({ hours: toTimeH, minutes: toTimeM }).asMinutes(),
      };
    },
    createTimeBorders(dayStr, workingHours, timezone) {
      console.log("#method @createTimeBorders");
      const minutesDayOffset = this.getMinutesDayOffset(workingHours);
      const createDateFormat = (minutesOffset) =>
        dayjs(dayStr)
          .add(minutesOffset, "minute")
          .format("YYYY-MM-DD HH:mm");
      const fromTimeUnix = dayjs
        .unix(
          dayjs.tz(createDateFormat(minutesDayOffset.from), timezone).unix()
        )
        .unix();
      const toTimeUnix = dayjs
        .unix(dayjs.tz(createDateFormat(minutesDayOffset.to), timezone).unix())
        .unix();

      return { fromTimeUnix, toTimeUnix };
    },
    createRequestTimeBorders(dayStr) {
      return this.createTimeBorders(dayStr, "00-24", this.panelTimezone);
    },
    createWorkTimeBorders(dayStr) {
      const workingHours =
        this.workingHoursMode === "full_day" ? "00-24" : this.localWorkingHours;

      return this.createTimeBorders(
        dayStr,
        workingHours,
        this.panelTimezone
      );
    },
    createRequestParamsForDay(dayStr) {
      return {
        day: this.isDateToday(dayStr) ? null : dayStr,
        users: this.params.users,
        eventTypes: this.params.eventTypes,
        ...this.createRequestTimeBorders(dayStr),
      };
    },
    async requestEventsForDay(dayStr) {
      const events = await activityApi.getDayEvents(
        dayStr,
        this.createRequestParamsForDay(dayStr)
      );

      for (const managerId of this.allUsersIds) {
        if (!events[managerId]) {
          events[managerId] = [];
        }
      }

      const scaleBorders = this.createWorkTimeBorders(dayStr);
      const prepared = this.prepareEventsAndWorkTime(events, scaleBorders);

      return {
        day: dayStr,
        ...prepared,
      };
    },
    async requestMessageEventsForDay(dayStr) {
      if (!this.messageTypeIds.length) {
        return this.createEmptyDayData(dayStr);
      }

      const events = await activityApi.getDayEvents(dayStr, {
        ...this.createRequestParamsForDay(dayStr),
        users: this.allUsersIds,
        eventTypes: this.messageTypeIds,
      });

      for (const managerId of this.allUsersIds) {
        if (!events[managerId]) {
          events[managerId] = [];
        }
      }

      const scaleBorders = this.createWorkTimeBorders(dayStr);
      const prepared = this.prepareMessageEventsAndWorkTime(events, scaleBorders);

      return {
        day: dayStr,
        ...prepared,
      };
    },
    async requestMessageEventsForDaySafe(dayStr) {
      try {
        return await this.requestMessageEventsForDay(dayStr);
      } catch (error) {
        console.warn(`Не удалось загрузить сообщения за ${dayStr}`, error);
        return this.createEmptyDayData(dayStr);
      }
    },
    createEmptyDayData(dayStr) {
      const scaleBorders = this.createWorkTimeBorders(dayStr);
      const emptyEvents = {};

      for (const managerId of this.allUsersIds) {
        emptyEvents[managerId] = [];
      }

      return {
        day: dayStr,
        ...this.prepareEventsAndWorkTime(emptyEvents, scaleBorders),
      };
    },
    async requestEventsForDaySafe(dayStr, { fallbackToEmpty = false } = {}) {
      try {
        return await this.requestEventsForDay(dayStr);
      } catch (error) {
        if (!fallbackToEmpty) {
          throw error;
        }

        console.warn(`Не удалось загрузить данные за ${dayStr}`, error);
        return this.createEmptyDayData(dayStr);
      }
    },
    async requestData() {
      const st = Date.now();
      this.isLoadingData = true;
      this.loadError = "";

      try {
        const selectedDays = this.isPeriodMode
          ? this.selectedPeriodDays
          : [this.select__dayFrom];

        if (this.isPeriodMode) {
          const loaded = await Promise.all([
            Promise.all(
              selectedDays.map((day) =>
                this.requestEventsForDaySafe(day, { fallbackToEmpty: false })
              )
            ),
            Promise.all(
              this.comparisonPeriodDays.map((day) =>
                this.requestEventsForDaySafe(day, { fallbackToEmpty: true })
              )
            ),
            Promise.all(
              selectedDays.map((day) =>
                this.requestMessageEventsForDaySafe(day)
              )
            ),
          ]);
          this.periodDailyData = loaded[0];
          this.comparisonPeriodDailyData = loaded[1];
          this.periodMessageDailyData = loaded[2];
          this.dayMessageData = null;
          this.events = {};
          this.workTime = {};
        } else {
          const loaded = await Promise.all([
            this.requestEventsForDay(selectedDays[0]),
            this.requestMessageEventsForDaySafe(selectedDays[0]),
          ]);
          const dayData = loaded[0];
          this.periodDailyData = [];
          this.comparisonPeriodDailyData = [];
          this.periodMessageDailyData = [];
          this.dayMessageData = loaded[1];
          this.scaleBorders = this.createWorkTimeBorders(selectedDays[0]);
          this.events = dayData.events;
          this.workTime = dayData.workTime;
        }
      } catch (error) {
        console.error("Не удалось загрузить данные панели", error);
        this.loadError =
          error && error.message
            ? error.message
            : "Не удалось загрузить данные панели";
      } finally {
        this.isLoadingData = false;
      }
      console.log("@requestData()", Date.now() - st, "mc");
    },
    createIntervalUpdate() {
      clearInterval(this.dataUpdateIntervalId);
      clearInterval(this.settingsUpdateIntervalId);
      clearInterval(this.filterDataUpdateIntervalId);
      this.dataUpdateIntervalId = null;
      this.settingsUpdateIntervalId = null;
      this.filterDataUpdateIntervalId = null;

      if (this.selectedPeriodDays.includes(dayjs().format("YYYY-MM-DD"))) {
        // включаю обовление данных по интервалу, если выключено
        console.log(
          "#watch @select__day дата - сегодня - ВКЛ обновление данных по интервалу"
        );
        this.dataUpdateIntervalId = setInterval(async () => {
          await this.requestData();
        }, 1000 * this.intervalSec.data);
      } else {
        // выключаю обовление данных по интервалу, если выбрана дата не сегодня (т.к. обновляться могут только графики за сегодня)
        console.log(
          "#watch @select__day Старая дата - ВЫКЛ обновление данных по интервалу"
        );
      }
      this.settingsUpdateIntervalId = setInterval(async () => {
        this.isLoadingData = true;
        this.settings = await activityApi.getSettings();
        this.isLoadingData = false;
      }, 1000 * this.intervalSec.settings);

      this.filterDataUpdateIntervalId = setInterval(async () => {
        this.isLoadingData = true;
        const lookup = await activityApi.getLookup();
        this.usersByDeps = lookup.usersByDeps || [];
        this.eventTypesRaw = lookup.eventTypes || [];
        this.isLoadingData = false;
      }, 1000 * this.intervalSec.depsAndEventTypes);
    },
    createTitleMultiselect() {
      $(".tabs-users")[0].nextSibling.setAttribute(
        "title",
        "Закройте список для применения фильтра"
      );
      $(".tabs-event_types")[0].nextSibling.setAttribute(
        "title",
        "Закройте список для применения фильтра"
      );
    },
    syncPeriodParams() {
      this.params.dayFrom = this.select__dayFrom;
      this.params.dayTo = this.select__dayTo;
      this.params.day =
        this.viewMode === "period" || this.isDateToday(this.normalizedPeriod.from)
          ? null
          : this.normalizedPeriod.from;
      utils.setParamsFromLocalStorage(this.platformStorageScope, this.params);
    },
    prepareEventsAndWorkTime(eventsAndWorkTime, scaleBorders) {
      const events = {};
      const workTimeRaw = {};

      for (const managerId in eventsAndWorkTime) {
        events[managerId] = eventsAndWorkTime[managerId]
          .filter(({ typeId }) => +typeId !== -3)
          .map(this.normalizeEvent);
        // workTimeRaw[managerId] = eventsAndWorkTime[managerId].find(
        //   ({ typeId }) => +typeId === -3
        // );
        workTimeRaw[managerId] = eventsAndWorkTime[managerId]
          .filter(({ typeId }) => +typeId === -3)
          .map(this.normalizeEvent);
      }

      const workTime = this.transformWorkTime(workTimeRaw, scaleBorders);
      const eventsInWorkingTime = this.getEventsInWorkingTime(events, workTime);

      const mergedEvents = this.mergeOneTypeCustomEvents(eventsInWorkingTime);
      const paddedEvents = this.applySimpleActivePadding(
        mergedEvents,
        workTime
      );

      return { events: paddedEvents, workTime };
    },
    prepareMessageEventsAndWorkTime(eventsAndWorkTime, scaleBorders) {
      const events = {};
      const workTimeRaw = {};

      for (const managerId in eventsAndWorkTime) {
        events[managerId] = eventsAndWorkTime[managerId]
          .filter(({ typeId }) => +typeId !== -3)
          .map(this.normalizeEvent);
        workTimeRaw[managerId] = eventsAndWorkTime[managerId]
          .filter(({ typeId }) => +typeId === -3)
          .map(this.normalizeEvent);
      }

      const workTime = this.transformWorkTime(workTimeRaw, scaleBorders);
      const eventsInWorkingTime = this.getEventsInWorkingTime(events, workTime);
      const preparedEvents = {};

      for (const managerId in eventsInWorkingTime) {
        const managerWorkTime = workTime[managerId];

        preparedEvents[managerId] = eventsInWorkingTime[managerId]
          .map((event) => {
            if (Number(event.typeId) === 0) {
              const startAt = Math.max(
                event.startAt,
                managerWorkTime.fromTimeUnix
              );
              const endAt = Math.min(
                event.startAt + event.duration,
                managerWorkTime.toTimeUnix
              );

              return {
                ...event,
                originalStartAt: event.startAt,
                originalDuration: event.duration,
                messageAt: event.startAt,
                startAt,
                duration: Math.max(endAt - startAt, 0),
                messageCount: this.getMessageEventCount(event),
              };
            }

            const messageAt = event.startAt;
            const startAt = Math.max(
              messageAt - this.getMessagePaddingSec(),
              managerWorkTime.fromTimeUnix
            );
            const endAt = Math.min(
              messageAt + this.getMessagePaddingSec(),
              managerWorkTime.toTimeUnix
            );

            return {
              ...event,
              originalStartAt: event.startAt,
              originalDuration: event.duration,
              messageAt,
              startAt,
              duration: Math.max(endAt - startAt, 0),
              messageCount: this.getMessageEventCount(event),
            };
          })
          .filter((event) => event.duration > 0)
          .sort((a, b) => a.startAt - b.startAt);
      }

      return { events: preparedEvents, workTime };
    },
    divideEventsAndWorkTime(eventsAndWorkTime) {
      const prepared = this.prepareEventsAndWorkTime(
        eventsAndWorkTime,
        this.scaleBorders
      );

      this.events = prepared.events;
      this.workTime = prepared.workTime;
    },
    transformWorkTime(workTimeRaw, fallbackScaleBorders = this.scaleBorders) {
      const workTime = {};

      for (const managerId in workTimeRaw) {
        workTime[managerId] = fallbackScaleBorders;
      }

      return workTime;
    },
    getEventsInWorkingTime(events, workTime = this.workTime) {
      const eventsInWorkTime = {};
      for (const managerId in events) {
        eventsInWorkTime[managerId] = events[managerId].filter(
          (event) =>
            event.startAt > workTime[managerId].fromTimeUnix &&
            event.startAt < workTime[managerId].toTimeUnix
        );
      }

      return eventsInWorkTime;
    },
    applySimpleActivePadding(eventsRaw, workTime) {
      const events = {};
      const simpleActivePaddingSec = this.getSimpleActivePaddingSec();

      for (const managerId in eventsRaw) {
        const managerWorkTime = workTime[managerId];

        events[managerId] = eventsRaw[managerId]
          .map((event) => {
            if (this.getEventGroup(event) !== "simple_active") {
              return event;
            }

            const startAt = Math.max(
              event.startAt - simpleActivePaddingSec,
              managerWorkTime.fromTimeUnix
            );
            const endAt = Math.min(
              event.startAt + simpleActivePaddingSec,
              managerWorkTime.toTimeUnix
            );

            return {
              ...event,
              originalStartAt: event.originalStartAt || event.startAt,
              originalDuration: event.originalDuration || event.duration,
              startAt,
              duration: Math.max(endAt - startAt, 0),
            };
          })
          .filter((event) => event.duration > 0)
          .sort((a, b) => a.startAt - b.startAt);
      }

      return events;
    },
    createPeriodTimelineDay(dayData, managerId) {
      const workTime = dayData.workTime[managerId];
      const events = dayData.events[managerId] || [];

      if (!workTime) {
        return {
          day: dayData.day,
          segments: [],
          activeDuration: 0,
          visibleDuration: 0,
        };
      }

      const end = this.getNowEndForWorkTime(dayData.day, workTime);
      const scaleWidth = Math.max(workTime.toTimeUnix - workTime.fromTimeUnix, 1);
      const visibleDuration = Math.max(end - workTime.fromTimeUnix, 0);
      const visibleEvents = events.filter((event) => event.startAt < end);
      const segments = visibleEvents.map((event) => {
        const segmentStart = Math.max(event.startAt, workTime.fromTimeUnix);
        const segmentEnd = Math.min(event.startAt + event.duration, end);

        return {
          ...event,
          duration: Math.max(segmentEnd - segmentStart, 0),
          left: ((segmentStart - workTime.fromTimeUnix) / scaleWidth) * 100,
          width: (Math.max(segmentEnd - segmentStart, 0) / scaleWidth) * 100,
          group: this.getEventGroup(event),
        };
      });

      return {
        day: dayData.day,
        segments: segments.filter((segment) => segment.duration > 0),
        activeDuration: this.getEventsUnionDuration(segments),
        visibleDuration,
      };
    },
    mergeOneTypeCustomEvents(eventsRaw) {
      const events1raw = {};
      const events2raw = {};
      const events = {};
      for (const managerId in eventsRaw) {
        events1raw[managerId] = eventsRaw[managerId].filter(
          (event) => event.typeId === -1
        );

        events2raw[managerId] = eventsRaw[managerId].filter(
          (event) => event.typeId === -2
        );

        events[managerId] = eventsRaw[managerId].filter(
          (event) => event.typeId !== -1 && event.typeId !== -2
        );
      }

      for (const managerId in events1raw) {
        let currentEvent = null;
        for (let i = 0; i < events1raw[managerId].length; i++) {
          if (!currentEvent) {
            currentEvent = events1raw[managerId][i];
            continue;
          }

          if (i + 1 === events2raw[managerId].length) {
            events[managerId].push(currentEvent);
            currentEvent = null;
            continue;
          }

          if (
            events1raw[managerId][i].startAt -
              events1raw[managerId][i - 1].startAt <=
            61
          ) {
            currentEvent.duration =
              events1raw[managerId][i].startAt +
              events1raw[managerId][i].duration -
              currentEvent.startAt;
          } else {
            events[managerId].push(currentEvent);
            currentEvent = null;
          }
        }

        if (currentEvent) {
          events[managerId].push(currentEvent);
          currentEvent = null;
        }
      }

      for (const managerId in events2raw) {
        let currentEvent = null;
        for (let i = 0; i < events2raw[managerId].length; i++) {
          if (!currentEvent) {
            currentEvent = events2raw[managerId][i];
            continue;
          }

          if (i + 1 === events2raw[managerId].length) {
            events[managerId].push(currentEvent);
            currentEvent = null;
            continue;
          }

          if (
            events2raw[managerId][i].startAt -
              events2raw[managerId][i - 1].startAt <=
            61
          ) {
            currentEvent.duration =
              events2raw[managerId][i].startAt +
              events2raw[managerId][i].duration -
              currentEvent.startAt;
          } else {
            events[managerId].push(currentEvent);
            currentEvent = null;
          }
        }
        if (currentEvent) {
          events[managerId].push(currentEvent);
          currentEvent = null;
        }
      }

      return { ...events };
    },
  },
  async created() {
    this.isLoadingData = true;

    try {
      const loaded = await Promise.all([
        activityApi.getSettings(),
        activityApi.getLookup(),
        activityApi.getPortalStatus(),
      ]);
      this.settings = loaded[0];
      this.seedLocalPanelSettings();
      this.usersByDeps = loaded[1].usersByDeps || [];
      this.eventTypesRaw = loaded[1].eventTypes || [];

      if (!this.select__users.length) {
        this.select__users = this.allUsersIds;
      }

      if (!this.select__eventTypes.length) {
        this.select__eventTypes = this.allEventTypesIds;
      }

      await this.requestData();
      this.createIntervalUpdate();
    } catch (error) {
      console.error("Ошибка инициализации панели", error);
      this.loadError =
        error && error.message
          ? error.message
          : "Не удалось инициализировать панель";
    } finally {
      this.isLoadingData = false;
    }
  },
  async mounted() {
    document.addEventListener("click", this.closeSettingsOnOutsideClick);

    this.$nextTick(() => {
      $(".tabs-users").multiselect({
        ...multiselectOptions.userSchema,
        // onLoad: function (element) {
        //   $(element).val(window.appVue.select__users);
        //   // $('.tabs-users').multiselect('reload');
        // },
        onPlaceholder: function(element, placeholder) {
          if ($(element).val()) {
            const countSelect = $(element).val().length;
            if ($(element).find("option").length === countSelect) {
              placeholder[0].innerText = "Все пользователи";
            } else if (countSelect > 1) {
              const declUserTitle = utils.declOfNum(countSelect, [
                "пользователь",
                "пользователя",
                "пользователей",
              ]);
              placeholder[0].innerText = `${countSelect} ${declUserTitle}`;
            }
          }
        },
        onControlClose: function(element) {
          if ($(element).val()) {
            const selectedValues = $(element)
              .val()
              .map(Number);

            if ($(element).find("option").length === selectedValues.length) {
              window.appVue.select__users = window.appVue.allUsersIds;
            } else {
              $(".tabs-users").val(selectedValues);
              $(".tabs-users").multiselect("reload");

              window.appVue.createTitleMultiselect();
              window.appVue.select__users = selectedValues;
            }
          } else {
            // если ничего не выбрано после закрытия - восстанавливаем предыдущий выбор
            $(".tabs-users").val(window.appVue.select__users);
            $(".tabs-users").multiselect("reload");

            window.appVue.createTitleMultiselect();
          }
        },
      });
      $(".tabs-event_types").multiselect({
        ...multiselectOptions.eventTypesSchema,
        // onLoad: function (element) {
        //   $(element).val(window.appVue.select__eventTypes);
        //   // $('.tabs-users').multiselect('reload');
        // },
        onPlaceholder: function(element, placeholder) {
          if ($(element).val()) {
            var countSelect = $(element).val().length;
            if ($(element).find("option").length === countSelect) {
              placeholder[0].innerText = "Список событий";
            } else if (countSelect > 1) {
              const declEventTypesTitle = utils.declOfNum(countSelect, [
                "событие",
                "события",
                "событий",
              ]);
              placeholder[0].innerText = `${countSelect} ${declEventTypesTitle}`;
            }
          }
        },
        onControlClose: function(element) {
          if ($(element).val()) {
            const selectedValues = $(element)
              .val()
              .map(Number);

            if ($(element).find("option").length != selectedValues.length) {
              $(".tabs-event_types").val(selectedValues);
              $(".tabs-event_types").multiselect("reload");

              window.appVue.createTitleMultiselect();
              window.appVue.select__eventTypes = selectedValues;
            } else {
              window.appVue.select__eventTypes = window.appVue.allEventTypesIds;
            }
          } else {
            // если ничего не выбрано после закрытия - восстанавливаем предыдущий выбор
            $(".tabs-event_types").val(window.appVue.select__eventTypes);
            $(".tabs-event_types").multiselect("reload");

            window.appVue.createTitleMultiselect();
          }
        },
      });

      utils.addTooltips($);
      // utils.changeStatTimeFormatFroTabletMediaQuery(this);
      // отключил т.к. длинный формат выглядит слишком громоздко даже для 1024px
    });
    this.$nextTick(() => {
      this.createTitleMultiselect();
    });
  },
  updated() {
    utils.addTooltips($);
  },
  watch: {
    allUsersIds(newValue, oldValue) {
      console.log("#watch @allUsersIds");
      if (this.isArrayEqual(newValue, oldValue)) {
        console.log("#watch @allUsersIds.  не изменился");

        this.$nextTick(() => {
          // $('.tabs-users').val(this.select__users);
          // $('.tabs-users').multiselect('reload');
        });
        return;
      }
      const valueToSelect = this.select__users.length
        ? this.select__users
        : newValue;

      this.$nextTick(() => {
        $(".tabs-users").val(valueToSelect);
        $(".tabs-users").multiselect("reload");

        this.createTitleMultiselect();
      });
    },
    allEventTypesIds(newValue, oldValue) {
      if (this.isArrayEqual(newValue, oldValue)) {
        console.log("#watch @allEventTypesIds. не изменился");

        this.$nextTick(() => {
          // $('.tabs-users').val(this.select__eventTypes);
          $(".tabs-event_types").multiselect("reload");

          this.createTitleMultiselect();
        });
        return;
      }
      console.log(
        "#watch @allEventTypesIds. изменился. Делаю выбранными в мультисписке Типов событий @allEventTypesIds "
      );
      const valueToSelect = this.select__eventTypes.length
        ? this.select__eventTypes
        : newValue;

      this.$nextTick(() => {
        $(".tabs-event_types").val(valueToSelect);
        $(".tabs-event_types").multiselect("reload");

        this.createTitleMultiselect();
      });
    },
    async select__users(newValue, oldValue) {
      if (this.isArrayEqual(newValue, oldValue)) {
        return;
      }

      if (this.isArrayEqual(newValue, this.allUsersIds)) {
        this.params.users = null;
        console.log(
          "#watch @select__users - все пользователи, делаю @params.users = null"
        );
      } else {
        console.log(
          "#watch @select__users, делаю @params.users = @select__users"
        );
        this.params.users = newValue;
      }
      utils.setParamsFromLocalStorage(this.platformStorageScope, this.params);

      if (oldValue.length) {
        console.log(
          "#watch @select__users изменилось - делаем запрос",
          newValue
        );

        await this.requestData();
      }
    },
    async select__eventTypes(newValue, oldValue) {
      if (this.isArrayEqual(newValue, oldValue)) {
        return;
      }

      if (this.isArrayEqual(newValue, this.allEventTypesIds)) {
        console.log(
          "#watch @select__eventTypes - все пользователи, делаю @params.users = null"
        );

        this.params.eventTypes = null;
      } else {
        console.log(
          "#watch @select__eventTypes, делаю @params.users = @select__users"
        );
        this.params.eventTypes = newValue;
      }
      utils.setParamsFromLocalStorage(this.platformStorageScope, this.params);

      if (oldValue.length) {
        console.log(
          "#watch @select__eventTypes изменилось - делаем запрос",
          newValue
        );

        await this.requestData();
      }
    },
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeSettingsOnOutsideClick);
    clearInterval(this.dataUpdateIntervalId);
    clearInterval(this.settingsUpdateIntervalId);
    clearInterval(this.filterDataUpdateIntervalId);
  },
};

export default mainVueApp;
</script>

<style>
@import "./styles/main.css";
@import "./styles/fonts.css";
</style>
